import React from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

export default function AlertModal({
    isOpen,
    onClose,
    title = 'Alert',
    message,
    type = 'info', // 'success', 'error', 'warning', 'info'
    confirmText = 'OK',
    onConfirm,
    showCancel = false,
    cancelText = 'Cancel',
    onCancel
}) {
    if (!isOpen) return null;

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        onClose();
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle size={48} style={{ color: '#10b981' }} />;
            case 'error':
                return <AlertCircle size={48} style={{ color: '#ef4444' }} />;
            case 'warning':
                return <AlertTriangle size={48} style={{ color: '#f59e0b' }} />;
            default:
                return <Info size={48} style={{ color: '#3b82f6' }} />;
        }
    };

    const getColor = () => {
        switch (type) {
            case 'success':
                return '#10b981';
            case 'error':
                return '#ef4444';
            case 'warning':
                return '#f59e0b';
            default:
                return '#3b82f6';
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={handleOverlayClick}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '24px',
                animation: 'fadeIn 0.2s ease-out'
            }}
        >
            <div
                className="modal"
                style={{
                    background: 'var(--bg-card)',
                    borderRadius: '24px',
                    maxWidth: '420px',
                    width: '100%',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s ease-out'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '24px 24px 16px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ flex: 1 }}>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            marginBottom: '4px'
                        }}>
                            {title}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--bg-hover)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--text-muted)';
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{
                    padding: '32px 24px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: `${getColor()}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        {getIcon()}
                    </div>
                    <p style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: 'var(--text-secondary)',
                        margin: 0
                    }}>
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '16px 24px 24px',
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                }}>
                    {showCancel && (
                        <button
                            onClick={handleCancel}
                            className="btn btn-secondary"
                            style={{
                                minWidth: '100px',
                                background: 'transparent',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border)',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                transition: 'all 0.2s',
                                padding: '10px 16px',
                                fontWeight: '500'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--bg-secondary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        onClick={handleConfirm}
                        className="btn btn-primary"
                        style={{
                            minWidth: '100px',
                            background: getColor(),
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            fontWeight: '500',
                            transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                        }}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
        </div>
    );
}
